import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowRight, Building2, ClipboardCheck, RefreshCw, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { getRequestAuthToken } from '../../auth/appliedAuthToken';
import { AWS_ADMIN_COMMISSIONING_PATH, AWS_ADMIN_USERS_PATH, AWS_CLIENTS_PATH } from '../../config/frontend';
import { useAuth } from '../../contexts/AuthContext';

interface AdminOverviewResponse {
  total_users: number;
  client_organizations: number;
}

type LoadState = 'loading' | 'ready' | 'unavailable';

const AdminDashboardAws: React.FC = () => {
  const { t } = useTranslation(['admin_common', 'common']);
  const { user } = useAuth();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [data, setData] = useState<AdminOverviewResponse | null>(null);
  const [reloadEpoch, setReloadEpoch] = useState(0);

  useEffect(() => {
    if (user?.role !== 'superadmin') return;

    const token = getRequestAuthToken();
    if (!token) {
      setData(null);
      setLoadState('unavailable');
      return;
    }

    let mounted = true;
    setLoadState('loading');

    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/overview`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('admin_overview_unavailable');
        return response.json() as Promise<AdminOverviewResponse>;
      })
      .then((payload) => {
        if (!mounted) return;
        if (
          !Number.isInteger(payload.total_users)
          || payload.total_users < 0
          || !Number.isInteger(payload.client_organizations)
          || payload.client_organizations < 0
        ) {
          throw new Error('admin_overview_invalid');
        }
        setData(payload);
        setLoadState('ready');
      })
      .catch(() => {
        if (!mounted) return;
        setData(null);
        setLoadState('unavailable');
      });

    return () => {
      mounted = false;
    };
  }, [user?.role, reloadEpoch]);

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-dashboard-denied">
        <div className="text-center">
          <Shield className="mx-auto mb-4 h-14 w-14 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900">{t('admin_common:accessDenied')}</h1>
          <p className="mt-2 text-gray-600">{t('admin_common:superadminRequired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6" data-testid="admin-dashboard-aws">
      <section>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('admin_common:title')}</h1>
        <p className="mt-1 text-sm text-gray-600">{t('admin_common:subtitle')}</p>
      </section>

      {loadState === 'loading' && (
        <section
          className="flex min-h-[180px] items-center justify-center rounded-2xl border border-gray-200 bg-white"
          data-testid="admin-dashboard-loading"
          aria-label={t('common:loading')}
        >
          <RefreshCw className="h-7 w-7 animate-spin text-gray-400" />
        </section>
      )}

      {loadState === 'unavailable' && (
        <section
          className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800"
          data-testid="admin-dashboard-unavailable"
          role="status"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h2 className="font-semibold">{t('admin_common:overview.unavailableTitle')}</h2>
              <p className="mt-1 text-sm">{t('admin_common:overview.unavailableMessage')}</p>
              <button
                type="button"
                onClick={() => setReloadEpoch((value) => value + 1)}
                className="mt-3 inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-semibold text-red-700"
                data-testid="admin-dashboard-retry"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common:refresh')}
              </button>
            </div>
          </div>
        </section>
      )}

      {loadState === 'ready' && data && (
        <>
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <article className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="admin-stat-users">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('admin_common:quickStats.totalUsers')}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{data.total_users}</p>
                </div>
                <Users className="h-6 w-6 text-gray-400" />
              </div>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="admin-stat-clients">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('admin_common:quickStats.clientOrganizations')}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{data.client_organizations}</p>
                </div>
                <Building2 className="h-6 w-6 text-gray-400" />
              </div>
            </article>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Link
              to={AWS_CLIENTS_PATH}
              data-testid="admin-open-clients"
              className="group flex min-h-[128px] items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                <Building2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">{t('admin_common:sections.clients.title')}</h2>
                <p className="mt-1 text-sm text-gray-600">{t('admin_common:sections.clients.description')}</p>
              </div>
              <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </Link>

            <Link
              to={AWS_ADMIN_USERS_PATH}
              data-testid="admin-open-users"
              className="group flex min-h-[128px] items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                <Users className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">{t('admin_common:sections.users.title')}</h2>
                <p className="mt-1 text-sm text-gray-600">{t('admin_common:sections.users.description')}</p>
              </div>
              <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </Link>

            <Link
              to={AWS_ADMIN_COMMISSIONING_PATH}
              data-testid="admin-open-commissioning"
              className="group flex min-h-[128px] items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                <ClipboardCheck className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">{t('admin_common:commissioning.title')}</h2>
                <p className="mt-1 text-sm text-gray-600">{t('admin_common:commissioning.navDescription')}</p>
              </div>
              <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminDashboardAws;
